import { Answers, Task, ValidationType, WorkModel } from 'models/task.model';
import {
  DraftPredefinedOptionsValidation,
  DraftTaskValidationField,
} from './task.type';

export const TASK_TITLE_CHARACTER_LIMIT = 100;
export const TASK_DESCRIPTION_CHARACTER_LIMIT = 900;
export const TASK_ERROR_MESSAGE_CHARACTER_LIMIT = 300;
export const TASK_COMMENT_CHARACTER_LIMIT = 300;
export const TASK_SUCCESS_MESSAGE_CHARACTER_LIMIT = 300;

export const ONE_OF_ANSWERS_MAX_QUANTITY = 20;
export const ONE_OF_ANSWER_MAX_LENGTH = 20;
export const PREDEFINED_ANSWER_MAX_LENGTH = 300;
export const PREDEFINED_CORRECT_ANSWERS_QUANTITY = 1;
export const PREDEFINED_INCORRECT_ANSWERS_MAX_QUANTITY = 20;

export const validateTitle = (
  title: string,
  fullValidation = false,
): string => {
  if (fullValidation && !title.length) {
    return 'Title is required';
  }

  if (title.length > TASK_TITLE_CHARACTER_LIMIT) {
    return `Title must be less than ${TASK_TITLE_CHARACTER_LIMIT} characters long. You are ${
      title.length - TASK_TITLE_CHARACTER_LIMIT
    } characters over`;
  }

  return '';
};

export const validateDescription = (
  description: string,
  fullValidation = false,
): string => {
  if (fullValidation && !description.length) {
    return 'Description is required';
  }

  if (description.length > TASK_DESCRIPTION_CHARACTER_LIMIT) {
    return `Description must be less than ${TASK_DESCRIPTION_CHARACTER_LIMIT} characters long. You are ${
      description.length - TASK_DESCRIPTION_CHARACTER_LIMIT
    } characters over`;
  }

  return '';
};

export const validateWorkModels = (
  workModels: WorkModel[],
  fullValidation = false,
): string => {
  if (fullValidation && !workModels.length) {
    return 'Type is required';
  }

  return '';
};

export const validateErrorMessage = (errorMessage: string): string => {
  if (errorMessage.length > TASK_ERROR_MESSAGE_CHARACTER_LIMIT) {
    return `Error message must be less than ${TASK_ERROR_MESSAGE_CHARACTER_LIMIT} characters long. You are ${
      errorMessage.length - TASK_ERROR_MESSAGE_CHARACTER_LIMIT
    } characters over`;
  }

  return '';
};

export const validateSuccessMessage = (successMessage: string): string => {
  if (successMessage?.length > TASK_SUCCESS_MESSAGE_CHARACTER_LIMIT) {
    return `Success message must be less than ${TASK_SUCCESS_MESSAGE_CHARACTER_LIMIT} characters long. You are ${
      successMessage.length - TASK_SUCCESS_MESSAGE_CHARACTER_LIMIT
    } characters over`;
  }

  return '';
};

export const validateComment = (comment: string): string => {
  if (comment.length > TASK_COMMENT_CHARACTER_LIMIT) {
    return `Comment must be less than ${TASK_COMMENT_CHARACTER_LIMIT} characters long. You are ${
      comment.length - TASK_COMMENT_CHARACTER_LIMIT
    } characters over`;
  }

  return '';
};

export const validateTask = (
  task: Task,
  fullValidation = true,
): DraftTaskValidationField => {
  const {
    title,
    description,
    workModels,
    validator,
    answers,
    errorMessage,
    successMessage,
    comment,
  } = task;

  return {
    title: validateTitle(title, fullValidation),
    description: validateDescription(description, fullValidation),
    workModels: validateWorkModels(workModels, fullValidation),
    answers: validateAnswer(validator, answers, fullValidation),
    errorMessage: validateErrorMessage(errorMessage),
    successMessage: validateSuccessMessage(successMessage),
    comment: validateComment(comment),
  };
};

export const isBasicValidationType = (validator: ValidationType): boolean => {
  return !(
    validator.includes('CONTAINS_ONE_OF') ||
    validator.includes('MATCHES_ONE_OF')
  );
};

export const validateAnswer = (
  validator: ValidationType,
  answers?: Answers,
  fullValidation = false,
): string => {
  if (isBasicValidationType(validator)) {
    return '';
  }

  const correctAnswers = answers?.correctAnswers;
  const isAnswerPresent = Boolean(correctAnswers && correctAnswers.length > 0);
  const isAnswerMissing = fullValidation && !isAnswerPresent;

  if (isAnswerMissing) {
    return 'At least 1 answer is required';
  }

  return '';
};

export const validatePredefinedOption = (
  answers?: Answers,
  fullValidation = true,
): DraftPredefinedOptionsValidation => {
  const noError: DraftPredefinedOptionsValidation = {
    predefinedOptionCorrectAnswer: '',
    predefinedOptionIncorrectAnswer: '',
  };

  const isValidationSkipped = !fullValidation || !answers;

  if (isValidationSkipped) {
    return noError;
  }

  const { correctAnswers, incorrectAnswers } = answers;
  const isAnswerEmpty = (answer: string[] | undefined) => {
    return Boolean(!answer || answer.length < 1);
  };

  if (isAnswerEmpty(correctAnswers)) {
    noError.predefinedOptionCorrectAnswer =
      'Should be at least 1 correct answer';
  }

  if (isAnswerEmpty(incorrectAnswers)) {
    noError.predefinedOptionIncorrectAnswer =
      'Should be at least 1 incorrect answer';
  }

  return noError;
};

export const setEmptyAnswers = (
  validator: ValidationType,
): Answers | undefined => {
  const isContainsOrMatches =
    validator.includes('CONTAINS_ONE_OF') ||
    validator.includes('MATCHES_ONE_OF');
  const isPredefinedOptions = validator.includes('PREDEFINED_OPTIONS');

  if (isContainsOrMatches) {
    return { correctAnswers: [] };
  }

  if (isPredefinedOptions) {
    return { correctAnswers: [], incorrectAnswers: [] };
  }

  return undefined; // ACCEPT_BUTTON, ANY_SYMBOLS, ONLY_NUMBER
};
